import { useContext, useState as classicUseState } from 'react';

import { useState } from '@hookstate/core';
import ReactTooltip from 'react-tooltip';

import { Icon as BlueIcon } from '@blueprintjs/core';
import SearchIcon from '@assets/svg/table-search.svg';
import CloseImage from '@assets/svg/small-cross.svg';

import { NavigationContext } from '@layout/navigation/navigation';
import { InviteUserRole } from '@models/Invite.model';
import { CurrentUsers } from '../CurrentUsers/CurrentUsers';
import { Invitations } from '../Invitations/Invitations';

import { InviteUserDialog } from './Components/InviteUserDialog/InviteUserDialog';

import {
  Container,
  Title,
  Line,
  InputContainer,
  InputSearch,
  Row,
  Button,
  Divider,
  Back,
  Indicator,
  Item,
  Label,
  LabelText,
  Tabs,
  TabsContainer,
  Body,
  SearchIconContainer,
  Close,
  AddUserWrapper,
} from './UserManagement.styles';

const UserManagement = () => {
  const page = useState<'current_users' | 'invitations'>('current_users');

  const [openInviteUser, setOpenInviteUser] = classicUseState<boolean>(false);
  const [searchTextModel, setSearchTextModel] = classicUseState('');
  const [searchText, setSearchText] = classicUseState('');

  const { role } = useContext(NavigationContext);

  return (
    <Container>
      <Line>
        <Title>User Management</Title>
        <Row>
          <InputContainer>
            <SearchIconContainer>
              <img src={SearchIcon} alt="search" />
            </SearchIconContainer>
            <InputSearch
              placeholder="Find user"
              value={searchTextModel}
              onChange={e => setSearchTextModel(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  setSearchText(searchTextModel);
                }
              }}
              autoFocus
            />
            {searchTextModel !== '' && (
              <Close
                src={CloseImage}
                alt="close"
                onClick={() => {
                  if (setSearchTextModel) setSearchTextModel('');
                  if (setSearchText) setSearchText('');
                }}
              />
            )}
          </InputContainer>

          <AddUserWrapper
            data-tip
            data-for="inviteUserTooltip"
            disabled={![InviteUserRole.ADMIN].includes(role)}
          >
            <Button
              onClick={() => setOpenInviteUser(true)}
              disabled={![InviteUserRole.ADMIN].includes(role)}
            >
              <BlueIcon icon="new-person" color="#8F95B2" />
            </Button>
            <ReactTooltip
              className="tooltip"
              id="inviteUserTooltip"
              effect="solid"
              multiline
              place="bottom"
              disable={[InviteUserRole.ADMIN].includes(role)}
            >
              Only admins can invite users.
            </ReactTooltip>
          </AddUserWrapper>
        </Row>
      </Line>
      <Divider height="24px" />

      <TabsContainer>
        <Tabs>
          <Item
            onClick={() => {
              page.set('current_users');
            }}
          >
            <Label>
              <LabelText isActive={page.get() === 'current_users'}>
                Current Users
              </LabelText>
            </Label>

            <Indicator isActive={page.get() === 'current_users'} />
          </Item>
          <Item
            onClick={() => {
              page.set('invitations');
            }}
          >
            <Label>
              <LabelText isActive={page.get() === 'invitations'}>
                Invitations
              </LabelText>
            </Label>
            <Indicator isActive={page.get() === 'invitations'} />
          </Item>
        </Tabs>
        <Back />
      </TabsContainer>

      <Body show={page.get() === 'current_users'}>
        <CurrentUsers searchText={searchText} />
      </Body>

      <Body show={page.get() === 'invitations'}>
        <Invitations />
      </Body>

      {openInviteUser && (
        <InviteUserDialog
          open={openInviteUser}
          onClose={() => {
            setOpenInviteUser(false);
          }}
        />
      )}
    </Container>
  );
};

export { UserManagement };
